import { useEffect, useMemo, useState } from 'react';
import { BiCalendarEvent, BiListCheck, BiWallet, BiWrench } from 'react-icons/bi';

import { StatTile } from '@/components/StatTile/StatTile';
import { PERMISSIONS, type Permission } from '@/permissions/permissions.catalog';
import { usePermissions } from '@/permissions/usePermissions';
import type { EventResponse } from '@/store/events/events.dto';
import { useEvents } from '@/store/events/events.store';
import type { Transaction } from '@/store/expenses-store/transactions/transactions.dto';
import { useTransactionsStore } from '@/store/expenses-store/transactions/transactions.store';
import { TaskStatus, type TaskResponse } from '@/store/tasks/tasks.dto';
import { useTasks } from '@/store/tasks/tasks.store';
import { isInflow } from '@pages/Expenses/TransactionsPage/utils/transactionColors';

import s from './AtAGlanceWidget.module.scss';

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
const SPARK_BUCKETS = 8;
const MONTH_SPARK_BUCKETS = 5;

const OPEN_TASK_STATUSES: TaskStatus[] = [
  TaskStatus.PENDING,
  TaskStatus.IN_PROGRESS,
  TaskStatus.ON_APPROVAL,
];

const isOpenTask = (task: TaskResponse) => OPEN_TASK_STATUSES.includes(task.status);

const toTime = (value?: string | null) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const taskDueTime = (task: TaskResponse) => toTime(task.deadline) ?? toTime(task.time_slot?.end);

const countSeries = (timestamps: number[], from: number, direction: 1 | -1) => {
  const buckets = new Array<number>(SPARK_BUCKETS).fill(0);

  timestamps.forEach((time) => {
    const index = Math.floor((direction * (time - from)) / WEEK_MS);
    if (index >= 0 && index < SPARK_BUCKETS) buckets[index] += 1;
  });

  const ordered = direction === 1 ? buckets : buckets.reverse();
  let running = 0;
  return ordered.map((count) => (running += count));
};

const amountSeries = (entries: { time: number; amount: number }[], from: number) => {
  const buckets = new Array<number>(MONTH_SPARK_BUCKETS).fill(0);

  entries.forEach(({ time, amount }) => {
    const index = Math.min(
      MONTH_SPARK_BUCKETS - 1,
      Math.max(0, Math.floor((time - from) / WEEK_MS)),
    );
    buckets[index] += amount;
  });

  let running = 0;
  return buckets.map((sum) => (running += sum));
};

const nextEventLabel = (starts: number[], now: number) => {
  if (starts.length === 0) return 'No upcoming events';

  const days = Math.ceil((Math.min(...starts) - now) / DAY_MS);
  if (days <= 0) return 'Next: today';
  if (days === 1) return 'Next: tomorrow';
  return `Next: in ${days} days`;
};

const formatAmount = (value: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);

const upcomingStarts = (events: EventResponse[], now: number) =>
  events
    .map((event) => toTime(event.time_slot?.start))
    .filter((time): time is number => time !== null && time >= now);

const monthOutflow = (transactions: Transaction[], monthStart: number) =>
  transactions.reduce<{ time: number; amount: number; currency: string }[]>((acc, transaction) => {
    if (isInflow(transaction.type)) return acc;

    const time = toTime(transaction.finalized_at) ?? toTime(transaction.created_at);
    if (time === null || time < monthStart) return acc;

    const amount = Math.abs(Number.parseFloat(String(transaction.amount?.number ?? '0')));
    acc.push({
      time,
      amount: Number.isNaN(amount) ? 0 : amount,
      currency: transaction.amount?.currency || 'USD',
    });
    return acc;
  }, []);

/**
 * The four tiles come from three services with three different gates, so the widget is not
 * one permission but four. A tile whose service would answer 403 is left out rather than
 * shown empty, and the spend tile's request is not made at all — the widget itself stays
 * open because the events tile rests on a base permission everyone holds.
 */
const TILE_RIGHTS: Record<string, readonly Permission[]> = {
  tasks: [PERMISSIONS.activity.ExternalTasks, PERMISSIONS.activity.Tasks],
  maintenance: [PERMISSIONS.activity.ExternalTasks, PERMISSIONS.activity.Tasks],
  expenses: [PERMISSIONS.wallet.TransactionsBasic, PERMISSIONS.wallet.Transactions],
};

export const AtAGlanceWidget = () => {
  const eventsList = useEvents((state) => state.eventsList);
  const tasksList = useTasks((state) => state.tasksList);
  const transactions = useTransactionsStore((state) => state.transactions);
  const getAllTransactions = useTransactionsStore((state) => state.getAllTransactions);

  const { canAny } = usePermissions();
  const canSeeSpend = canAny([...TILE_RIGHTS.expenses]);

  const [transactionsReady, setTransactionsReady] = useState(false);
  const [now] = useState(() => Date.now());

  useEffect(() => {
    if (!canSeeSpend) return;
    void getAllTransactions().finally(() => setTransactionsReady(true));
  }, [getAllTransactions, canSeeSpend]);

  const stats = useMemo(() => {
    const today = new Date(now);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).getTime();

    const events = eventsList?.items;
    const tasks = tasksList?.items;

    const starts = events ? upcomingStarts(events, now) : [];

    const openTasks = tasks?.filter((task) => !task.equipment && isOpenTask(task));
    const inProgress = openTasks?.filter((task) => task.status === TaskStatus.IN_PROGRESS).length;

    const maintenanceTasks = tasks?.filter((task) => task.equipment != null && isOpenTask(task));
    const overdue = maintenanceTasks?.filter((task) => {
      const due = taskDueTime(task);
      return due !== null && due < now;
    }).length;

    const outflow = transactionsReady ? monthOutflow(transactions, monthStart) : null;
    const spent = outflow?.reduce((sum, entry) => sum + entry.amount, 0) ?? 0;
    const currency = outflow?.[0]?.currency ?? 'USD';

    return [
      {
        id: 'events',
        label: 'Upcoming Events',
        icon: <BiCalendarEvent />,
        value: events ? starts.length : undefined,
        sublabel: events ? nextEventLabel(starts, now) : undefined,
        sparkline: starts.length > 0 ? countSeries(starts, now, 1) : undefined,
        to: '/plan?view=week',
      },
      {
        id: 'tasks',
        label: 'Open Tasks',
        icon: <BiListCheck />,
        value: openTasks?.length,
        sublabel: openTasks ? `${inProgress} in progress` : undefined,
        sparkline: openTasks?.length
          ? countSeries(
              openTasks
                .map((task) => toTime(task.created_at))
                .filter((time): time is number => time !== null),
              now,
              -1,
            )
          : undefined,
        to: '/tasks',
      },
      {
        id: 'maintenance',
        label: 'Maintenance Due',
        icon: <BiWrench />,
        value: maintenanceTasks?.length,
        sublabel: maintenanceTasks ? `${overdue} overdue` : undefined,
        sparkline: maintenanceTasks?.length
          ? countSeries(
              maintenanceTasks
                .map((task) => toTime(task.created_at))
                .filter((time): time is number => time !== null),
              now,
              -1,
            )
          : undefined,
        to: '/maintain/schedule',
      },
      {
        id: 'expenses',
        label: 'Expenses This Month',
        icon: <BiWallet />,
        value: outflow ? formatAmount(spent, currency) : undefined,
        sublabel: outflow ? `${outflow.length} transactions` : undefined,
        sparkline: outflow?.length ? amountSeries(outflow, monthStart) : undefined,
        to: '/manage/transactions',
      },
    ].filter((stat) => {
      const rights = TILE_RIGHTS[stat.id];
      return !rights || canAny([...rights]);
    });
  }, [eventsList, tasksList, transactions, transactionsReady, now, canAny]);

  return (
    <section className={s.root} aria-label="At a glance">
      {/*<h2 className={s.sectionLabel}>At a glance</h2>*/}
      <div className={s.stats}>
        {stats.map((stat) => (
          <StatTile
            key={stat.id}
            label={stat.label}
            icon={stat.icon}
            value={stat.value}
            sublabel={stat.sublabel}
            reserveSublabel
            sparkline={stat.sparkline}
            to={stat.to}
          />
        ))}
      </div>
    </section>
  );
};

export default AtAGlanceWidget;
